import React, { useEffect, useRef, useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import { AlertTriangle } from "lucide-react";
import { createBatch, updateBatch, getBatches, getBatchById } from "../../../../../services/BatchService.js";
import { checkClash } from "../../../../../services/ClassroomManagementService.js";
import { getTrainers } from "../../../../../services/TrainerService.js";
import { getClassrooms, getClassroomById } from "../../../../../services/ClassroomService.js";
import { getcourses } from "../../../../../services/CourseModule.js";
import Modal from "../../components/Modal.jsx";
import DaysToggle from "../../components/DaysToggle.jsx";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import { combineDateAndTime } from "../../classroomSchedule.js";
import { clashNames } from "../../../../../utils/clashNames";

const trainerName = (t) => t?.trainerName || t?.name || t?.trainerEmail || "Unassigned";

// The LOCAL calendar date. toISOString() reads it back in UTC, which in IST
// is the previous day for anything before 05:30 -- a booking starting at
// 01:00 on the 3rd showed the 2nd in the date picker.
const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const toTimeInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/* A batch read from the API carries its course, trainer and classroom either
   as the joined record (usually an array of one) or as a bare id. */
const first = (v) => (Array.isArray(v) ? v[0] : v);
const idOf = (v) => {
  const x = first(v);
  if (x && typeof x === "object") return String(x.id || x._id || "");
  return x ? String(x) : "";
};
const recordOf = (v) => {
  const x = first(v);
  return x && typeof x === "object" ? x : null;
};

/* A seeded dropdown option only while it still describes the chosen value --
   otherwise a cleared field would keep showing the old label. */
const seedFor = (seed, value) => (seed && value && seed.value === value ? seed : null);

/* Two steps, in the order a booking is decided: who and where, then when.
   The lab is not a step of its own -- one field is not worth a screen. */
const STEPS = [
  { label: "Batch & lab", hint: "Pick a batch to fill in its course, trainer and lab, or enter them yourself." },
  { label: "Schedule", hint: "Which days, at what time, and over which dates." },
];

/* What each step needs before it may be left. Messages, not flags, so each
   field can say what is wrong right under itself. */
const stepErrors = (step, form) => {
  if (step === 1) {
    return {
      name: form.name.trim() ? "" : "Enter a batch name",
      trainerId: form.trainerId ? "" : "Pick a trainer",
      classroom: form.classroom ? "" : "Pick a lab or classroom",
    };
  }
  return {
    days: form.days.length ? "" : "Pick at least one day",
    clock: form.startClock && form.endClock && form.endClock > form.startClock
      ? ""
      : "The end time must be after the start time",
    dates: !form.startDate
      ? "Pick a start date"
      : form.endDate && form.endDate < form.startDate
        ? "The end date cannot be before the start date"
        : "",
  };
};
const hasErrors = (errors) => Object.values(errors).some(Boolean);

/* `prefill` starts a NEW booking already pointed somewhere -- the dashboard's
   Schedule on a free lab or trainer day passes
   { date, days, startClock, endClock, classroom?: {value,label}, trainer?: {value,label} }.
   An edit (`booking`) always wins over it. */
const BookingFormModal = ({ booking, prefill, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: booking?.name || "",
    courseId: booking?.courseId || "",
    trainerId: booking?.trainerId || (!booking && prefill?.trainer?.value) || "",
    classroom: booking?.classroom || (!booking && prefill?.classroom?.value) || "",
    days: booking?.days
      ? String(booking.days).split(",").map((d) => d.trim()).filter(Boolean)
      : (!booking && prefill?.days) || [],
    startDate: toDateInput(booking?.startTime) || (!booking && prefill?.date) || new Date().toISOString().slice(0, 10),
    endDate: toDateInput(booking?.endTime) || (!booking && prefill?.date) || "",
    startClock: toTimeInput(booking?.startTime) || (!booking && prefill?.startClock) || "09:00",
    endClock: toTimeInput(booking?.endTime) || (!booking && prefill?.endClock) || "11:00",
  });
  const [saving, setSaving] = useState(false);
  const [clashes, setClashes] = useState([]);
  const [step, setStep] = useState(1);
  // The step as it was when the current click began. A submit that arrives
  // in the same click that just advanced the step reads the old value here
  // and is treated as Continue, never as a save.
  const stepRef = useRef(1);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  // A step's messages show once someone has tried to leave it -- an
  // untouched form is not a list of errors.
  const [checked, setChecked] = useState({ 1: false, 2: false });

  // The batch picked from the Batch dropdown. On a new booking it is also
  // what gets saved: picking an existing batch books THAT batch rather than
  // creating a second one with the same name. On an edit it only fills the
  // form; the booking being edited is still the one saved.
  const [batchId, setBatchId] = useState(booking?.id || "");
  const [filling, setFilling] = useState(false);
  const [roomNote, setRoomNote] = useState("");
  const fillSeq = useRef(0);

  // The label behind every chosen value. A step's fields unmount when the
  // step changes, so each dropdown is re-seeded from here on the way back
  // and still shows what was picked.
  const [seeds, setSeeds] = useState({
    batch: booking?.id ? { value: booking.id, label: booking.name || "This booking" } : null,
    course: booking?.courseId ? { value: booking.courseId, label: booking.courseName || booking.courseId } : null,
    trainer: booking?.trainerId
      ? { value: booking.trainerId, label: booking.trainerName || booking.trainerId }
      : (!booking && prefill?.trainer) || null,
    room: booking?.classroom
      ? { value: booking.classroom, label: booking.classroomNo || booking.classroom }
      : (!booking && prefill?.classroom) || null,
  });
  const seed = (key, option) => setSeeds((s) => ({ ...s, [key]: option || null }));

  // A new booking that picked a batch saves to that batch.
  const saveTargetId = booking?.id || batchId || null;

  useEffect(() => {
    if (!form.trainerId && !form.classroom) { setClashes([]); return; }
    if (!form.days.length) { setClashes([]); return; }

    const startTime = combineDateAndTime(form.startDate, form.startClock);
    const endTime = combineDateAndTime(form.endDate || form.startDate, form.endClock);
    if (!startTime || !endTime) { setClashes([]); return; }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await checkClash({
          trainerId: form.trainerId,
          classroom: form.classroom,
          days: form.days.join(","),
          startTime,
          endTime,
          excludeBatchId: saveTargetId || undefined,
        });
        if (!cancelled) setClashes(result);
      } catch (error) {
        if (!cancelled) setClashes([]);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.trainerId, form.classroom, form.days, form.startDate, form.endDate, form.startClock, form.endClock, saveTargetId]);

  /**
   * Fill the form from a picked batch: its name, course and trainer, and its
   * days, dates and times when it has them. Its lab is filled only when the
   * lab is usable at those times -- in working order, and not already taken
   * by another booking. Otherwise the lab is left empty and the reason shown,
   * so nobody books into a room that is out of order or double-booked.
   */
  const applyBatch = async (id, option) => {
    const seq = ++fillSeq.current;
    setBatchId(id);
    setRoomNote("");
    if (!id) {
      seed("batch", null);
      return;
    }

    setFilling(true);
    try {
      let batch = option?.batch || null;
      if (!batch) {
        const res = await getBatchById(id);
        batch = res?.data?.data || res?.data || res || null;
      }
      if (seq !== fillSeq.current) return;
      if (!batch) {
        toast.error("Could not load that batch");
        return;
      }

      const course = recordOf(batch.courseId);
      const trainer = recordOf(batch.trainerId);
      const room = recordOf(batch.classroom);
      const courseId = idOf(batch.courseId);
      const trainerId = idOf(batch.trainerId);
      const roomId = idOf(batch.classroom);
      const batchDays = String(batch.days || "").split(",").map((d) => d.trim()).filter(Boolean);

      /* Opened with Schedule from a day on the dashboard (`prefill`), the
         day, weekday and slot are what was clicked, and a picked batch must
         not move them -- it fills in who and what, not when. Otherwise (a
         blank new booking, or an edit) the batch's own schedule comes too. */
      const keepWhen = !booking && Boolean(prefill?.date);
      /* Likewise the lab or trainer whose free day was clicked stays the one
         booked. Taking the batch's own lab/trainer here saved the booking
         onto a different row, and the clicked cell stayed free. */
      const keepRoom = keepWhen && Boolean(prefill?.classroom?.value);
      const keepTrainer = keepWhen && Boolean(prefill?.trainer?.value);
      const next = {
        name: batch.name || "",
        courseId,
        trainerId: keepTrainer ? prefill.trainer.value : trainerId,
        classroom: keepRoom ? prefill.classroom.value : "",
        days: keepWhen ? form.days : batchDays.length ? batchDays : form.days,
        startDate: keepWhen ? form.startDate : batch.startTime ? toDateInput(batch.startTime) : form.startDate,
        startClock: keepWhen ? form.startClock : batch.startTime ? toTimeInput(batch.startTime) : form.startClock,
        endDate: keepWhen ? form.endDate : batch.endTime ? toDateInput(batch.endTime) : form.endDate,
        endClock: keepWhen ? form.endClock : batch.endTime ? toTimeInput(batch.endTime) : form.endClock,
      };

      let note = "";
      if (keepRoom) {
        // The clicked lab was free that day; the clash check below the form
        // still flags anything booked into it meanwhile.
      } else if (!roomId) {
        note = "This batch has no lab assigned yet. Pick one below.";
      } else {
        const roomLabel = room?.classroomNo || "The batch's lab";
        try {
          const startTime = combineDateAndTime(next.startDate, next.startClock);
          const endTime = combineDateAndTime(next.endDate || next.startDate, next.endClock);
          const [roomRes, roomClashes] = await Promise.all([
            getClassroomById(roomId),
            startTime && endTime && next.days.length
              ? checkClash({
                  classroom: roomId,
                  days: next.days.join(","),
                  startTime,
                  endTime,
                  // The booking that will be saved does not clash with itself.
                  excludeBatchId: booking?.id || id,
                })
              : Promise.resolve([]),
          ]);
          if (seq !== fillSeq.current) return;

          const record = roomRes?.data;
          if (!record) {
            note = `${roomLabel} no longer exists. Pick another lab below.`;
          } else if (record.isActive === false) {
            note = `${record.classroomNo || roomLabel} is out of order, so it was not filled in. Pick another lab below.`;
          } else if (roomClashes.length) {
            note = `${record.classroomNo || roomLabel} is taken at these times by ${roomClashes
              .map((c) => c.name)
              .join(", ")}, so it was not filled in. Pick another lab below.`;
          } else {
            next.classroom = roomId;
          }
        } catch (error) {
          if (seq !== fillSeq.current) return;
          note = `Could not check whether ${roomLabel} is free. Pick the lab yourself.`;
        }
      }

      setForm((f) => ({ ...f, ...next }));
      setSeeds({
        batch: { value: id, label: batch.name || "Unnamed batch" },
        course: courseId ? { value: courseId, label: course?.name || courseId } : null,
        trainer: keepTrainer
          ? prefill.trainer
          : trainerId ? { value: trainerId, label: trainer ? trainerName(trainer) : trainerId } : null,
        room: keepRoom
          ? prefill.classroom
          : next.classroom ? { value: next.classroom, label: room?.classroomNo || next.classroom } : null,
      });
      setRoomNote(note);
    } catch (error) {
      if (seq === fillSeq.current) toast.error("Could not load that batch");
    } finally {
      if (seq === fillSeq.current) setFilling(false);
    }
  };

  const errors = stepErrors(step, form);
  const showErrors = checked[step];
  const errorFor = (key) => (showErrors && errors[key] ? errors[key] : "");

  /* Continue moves forward only when this step is complete.
     The strip goes back to a passed step; on an edit it opens any step. */
  const goNext = () => {
    if (filling) return;
    setChecked((c) => ({ ...c, [step]: true }));
    if (hasErrors(stepErrors(step, form))) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (filling || saving) return;
    // Enter on step 1 is a Continue, not a save -- and so is any submit
    // that reaches here before step 2 has actually been shown.
    if (step < STEPS.length || stepRef.current < STEPS.length) {
      goNext();
      return;
    }
    setChecked({ 1: true, 2: true });
    if (hasErrors(stepErrors(1, form))) {
      setStep(1);
      return;
    }
    if (hasErrors(stepErrors(2, form))) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        courseId: form.courseId,
        trainerId: form.trainerId,
        classroom: form.classroom,
        days: form.days.join(","),
        startTime: combineDateAndTime(form.startDate, form.startClock),
        endTime: combineDateAndTime(form.endDate || form.startDate, form.endClock),
      };
      if (saveTargetId) {
        await updateBatch(saveTargetId, payload);
        toast.success(booking ? "Booking updated" : "Batch booked");
      } else {
        await createBatch({
          ...payload,
          userId: "",
          mode: "Offline",
          meetingLink: "",
          meetingId: "",
          password: "",
        });
        toast.success("Booking created");
      }
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message?.message || "Failed to save booking");
    } finally {
      setSaving(false);
    }
  };

  // Same lab, same time is refused by the API, so Save stays off; the same
  // trainer at the same time is only a warning.
  const labClashes = clashes.filter((c) => c.lab);
  const trainerClashes = clashes.filter((c) => c.trainer && !c.lab);

  const lastStep = step === STEPS.length;
  const saveLabel = booking ? "Save changes" : batchId ? "Book batch" : "Create booking";

  return (
    <Modal
      over
      eyebrow={booking ? "Edit booking" : "New booking"}
      title={STEPS[step - 1].label}
      sub={STEPS[step - 1].hint}
      onClose={onClose}
      size="steady"
      labelledBy="cls-booking-title"
      footer={
        <>
          <div className="pgm-actions">
            <button type="button" className="pgm-btn" onClick={onClose}>
              Cancel
            </button>
            {step > 1 && (
              <button type="button" className="pgm-btn" onClick={() => setStep((s) => s - 1)}>
                Back
              </button>
            )}
          </div>
          {/* Different keys, so Continue and Save are two DOM buttons, never
              one reused. Reused, the click on Continue moved to step 2,
              React turned that same <button> into type="submit", and the
              browser's default action for the click -- run after the
              handler -- then submitted the form from step 1. */}
          {lastStep ? (
            <button
              key="save"
              type="submit"
              form="cls-booking-form"
              className="pg-btn pg-btn--main"
              disabled={saving || filling || labClashes.length > 0}
            >
              {saving ? "Saving..." : saveLabel}
            </button>
          ) : (
            <button key="continue" type="button" className="pg-btn pg-btn--main" onClick={goNext} disabled={filling}>
              {filling ? "Filling in..." : "Continue"}
            </button>
          )}
        </>
      }
    >
      {/* The submit button lives in the dialog footer, outside this form --
          the HTML form attribute is what still connects the two. */}
      <form id="cls-booking-form" className="att-form" noValidate onSubmit={submit}>
        {/* The steps. A passed step can be revisited; one ahead is reached
            only through Continue on a new booking, by its tab on an edit. */}
        <ol className="att-steps">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const state = n === step ? "is-now" : n < step ? "is-done" : "";
            return (
              <li className={`att-step-tab ${state}`.trim()} key={s.label}>
                <button type="button" disabled={n === step || (!booking && n > step)} onClick={() => setStep(n)}>
                  <span className="att-step-n">{n}</span>
                  {s.label}
                </button>
              </li>
            );
          })}
        </ol>

        {labClashes.length > 0 && (
          <p className="pg-notice pg-notice--bad">
            <AlertTriangle size={14} aria-hidden="true" />
            {labClashes[0].classroomNo || "This lab"} is already booked at this time by{" "}
            {clashNames(labClashes)}. Pick another lab or time to save.
          </p>
        )}
        {trainerClashes.length > 0 && (
          <p className="att-note att-note--warn">
            <AlertTriangle size={14} aria-hidden="true" />
            The trainer is also teaching {clashNames(trainerClashes)} at this time.
          </p>
        )}

        <div className="att-stage">
          {step === 1 && (
            <section className="att-step" key="step-1">
              <div className="pg-field">
                <label className="pg-label" htmlFor="bk-batch">Batch</label>
                <div id="bk-batch">
                  <AsyncPagedSelect
                    value={batchId}
                    onChange={applyBatch}
                    fetchPage={async (page, search) => {
                      const res = await getBatches(20, page, search, "name");
                      return { data: res?.data || [], pagination: res?.pagination };
                    }}
                    toOption={(b) => ({ value: b.id, label: b.name || "Unnamed batch", batch: b })}
                    initialOption={seedFor(seeds.batch, batchId)}
                    placeholder="Search a batch to fill in its course, trainer and lab"
                  />
                </div>
                {!booking && batchId && (
                  <p className="att-note">Saving books this existing batch rather than creating a new one.</p>
                )}
              </div>

              <div className="mcq-options">
                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-name">Batch name</label>
                  <input
                    id="bk-name"
                    className="pg-input"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Java-01A"
                  />
                  {errorFor("name") && <p className="att-error">{errorFor("name")}</p>}
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-course">Course</label>
                  <div id="bk-course">
                    <AsyncPagedSelect
                      value={form.courseId}
                      onChange={(id, option) => {
                        setForm((f) => ({ ...f, courseId: id }));
                        seed("course", option);
                      }}
                      fetchPage={(page, search) => getcourses(20, page, search)}
                      toOption={(c) => ({ value: c.id, label: c.name })}
                      initialOption={seedFor(seeds.course, form.courseId)}
                      placeholder="Select course"
                    />
                  </div>
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-trainer">Trainer</label>
                  <div id="bk-trainer">
                    <AsyncPagedSelect
                      value={form.trainerId}
                      onChange={(id, option) => {
                        setForm((f) => ({ ...f, trainerId: id }));
                        seed("trainer", option);
                      }}
                      fetchPage={(page, search) => getTrainers(20, page, search)}
                      toOption={(t) => ({ value: t.id, label: trainerName(t) })}
                      initialOption={seedFor(seeds.trainer, form.trainerId)}
                      placeholder="Select trainer"
                    />
                  </div>
                  {errorFor("trainerId") && <p className="att-error">{errorFor("trainerId")}</p>}
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-room">Lab / classroom</label>
                  <div id="bk-room">
                    <AsyncPagedSelect
                      value={form.classroom}
                      onChange={(id, option) => {
                        setForm((f) => ({ ...f, classroom: id }));
                        seed("room", option);
                        if (id) setRoomNote("");
                      }}
                      // Out-of-order rooms aren't offered for a new pick (the dropdown
                      // has no per-option disabled state, so they're filtered out page
                      // by page) -- but the seeded option keeps a booking's current room
                      // visible if it has since gone out of order.
                      fetchPage={async (page, search) => {
                        const { data, pagination } = await getClassrooms(20, page, search);
                        return { data: (data || []).filter((c) => c.isActive !== false), pagination };
                      }}
                      toOption={(c) => ({ value: c.id, label: c.classroomNo })}
                      initialOption={seedFor(seeds.room, form.classroom)}
                      placeholder="Select classroom"
                    />
                  </div>
                  {errorFor("classroom") && <p className="att-error">{errorFor("classroom")}</p>}
                </div>
              </div>

              {roomNote && (
                <p className="att-note att-note--warn">
                  <AlertTriangle size={14} aria-hidden="true" />
                  {roomNote}
                </p>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="att-step" key="step-2">
              <div className="pg-field">
                <label className="pg-label" htmlFor="bk-days">Days</label>
                <div id="bk-days">
                  <DaysToggle value={form.days} onChange={(days) => setForm((f) => ({ ...f, days }))} />
                </div>
                {errorFor("days") && <p className="att-error">{errorFor("days")}</p>}
              </div>

              <div className="mcq-options">
                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-start">Start time</label>
                  <input
                    id="bk-start"
                    type="time"
                    className="pg-input"
                    value={form.startClock}
                    onChange={(e) => setForm((f) => ({ ...f, startClock: e.target.value }))}
                  />
                </div>
                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-end">End time</label>
                  <input
                    id="bk-end"
                    type="time"
                    className="pg-input"
                    value={form.endClock}
                    onChange={(e) => setForm((f) => ({ ...f, endClock: e.target.value }))}
                  />
                  {errorFor("clock") && <p className="att-error">{errorFor("clock")}</p>}
                </div>
                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-from">Batch start date</label>
                  <input
                    id="bk-from"
                    type="date"
                    className="pg-input"
                    value={form.startDate}
                    onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  />
                </div>
                <div className="pg-field">
                  <label className="pg-label" htmlFor="bk-to">Batch end date</label>
                  <input
                    id="bk-to"
                    type="date"
                    className="pg-input"
                    value={form.endDate}
                    onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                  />
                  {errorFor("dates") && <p className="att-error">{errorFor("dates")}</p>}
                </div>
              </div>
            </section>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default BookingFormModal;
